import type { CSSProperties, ReactNode } from 'react';

interface DeltaValueProps {
  /** The delta value in the same units as the underlying row. */
  value: number | null;
  /** Render as `{value}`. If absent, callers should pass children for custom rendering. */
  children?: ReactNode;
  /**
   * If true, treat the row as a credit / "higher is better" row (residual
   * reclamation). Color rule inverts: positive delta = green (B captured
   * more credit), negative delta = red (B captured less credit).
   */
  isCredit?: boolean;
  /** Override the color rule with an explicit "winner" override. */
  winner?: 'a' | 'b' | 'tie' | null;
  fontSize?: number;
  fontWeight?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Phase 25.2 — color the Delta cell by the *absolute direction* of the value:
 *
 *   delta < 0  → accent (B's cost dropped vs A — favorable)
 *   delta > 0  → neg    (B's cost rose vs A — unfavorable)
 *   delta == 0 → ink-2
 *   delta == null → ink-3
 *
 * For credit / higher-is-better rows (Residual Value Reclaimed, Tax Benefit)
 * the rule inverts: a positive delta means B captured more credit, which is
 * favorable, so it renders accent.
 *
 * The `winner` parameter is accepted for caller back-compat but no longer
 * influences color. Color tracks direction; the winner label belongs in the
 * row's deltaSubtext, not the cell color. (Pre-25.2 the override made
 * cost-line cells green whenever B won the overall TCO — even on rows where
 * B was paying more — which inverted the natural cue.)
 */
export function deltaColor(
  value: number | null,
  opts: { isCredit?: boolean; winner?: 'a' | 'b' | 'tie' | null } = {},
): string {
  const { isCredit } = opts;
  if (value === null || !isFinite(value)) return 'var(--tco-ink-3)';
  if (Math.abs(value) < 1e-6) return 'var(--tco-ink-2)';
  const positive = value > 0;
  if (isCredit) return positive ? 'var(--tco-accent)' : 'var(--tco-neg)';
  return positive ? 'var(--tco-neg)' : 'var(--tco-accent)';
}

export default function DeltaValue({
  value,
  children,
  isCredit,
  winner,
  fontSize = 14,
  fontWeight = 400,
  className,
  style,
}: DeltaValueProps) {
  const color = deltaColor(value, { isCredit, winner: winner ?? null });
  return (
    <span
      className={`tco-num ${className ?? ''}`.trim()}
      style={{ color, fontSize, fontWeight, ...style }}
    >
      {children}
    </span>
  );
}
